<template lang="pug">
div.tm-shell(:class="{ 'is-zh': locale === 'zh' }")
  main.tm-main
    .tm-frame
      section.tm-hero.reveal
        .tm-hero-copy
          p.tm-eyebrow
            span.tm-eyebrow-line
            span {{ t('org.landing.hero.eyebrow') }}
          h1.tm-title
            span.tm-title-line {{ t('org.landing.hero.title_a') }}
            span.tm-title-line.is-accent {{ t('org.landing.hero.title_b') }}
          p.tm-lead {{ t('org.landing.hero.lead') }}
          .tm-actions
            button.tm-btn.is-primary(type="button" @click="startNow")
              span {{ t('org.landing.hero.cta_primary') }}
              Icon(name="lucide:arrow-right" aria-hidden="true")
            NuxtLink.tm-btn.is-ghost(to="/dedicated-servers") {{ t('org.landing.hero.cta_dedicated') }}
          p.tm-hero-note
            Icon.tm-note-icon(name="lucide:info" aria-hidden="true")
            span {{ t('org.landing.hero.note') }}
        .tm-hero-stage
          TeamsConsoleDemo

      section.tm-numbers.reveal(:aria-label="t('org.landing.numbers.aria')")
        .tm-number(v-for="(item, index) in numbers" :key="item.key" :style="{ '--delay': `${index * 90}ms` }")
          p.tm-number-value
            span {{ item.value }}
            span.tm-number-unit {{ item.unit }}
          p.tm-number-label {{ item.label }}

      section.tm-section.reveal(aria-labelledby="tm-flow-title")
        .tm-section-head
          div
            p.tm-eyebrow
              span.tm-eyebrow-line
              span {{ t('org.landing.flow.eyebrow') }}
            h2#tm-flow-title.tm-h2 {{ t('org.landing.flow.title') }}
          p.tm-section-lead {{ t('org.landing.flow.lead') }}
        ol.tm-flow
          li.tm-flow-step(v-for="(step, index) in flowSteps" :key="step.id" :style="{ '--i': index }")
            .tm-flow-node
              span.tm-flow-index {{ String(index + 1).padStart(2, '0') }}
            h3.tm-h3 {{ step.title }}
            p.tm-flow-desc {{ step.desc }}

      section.tm-section.reveal(aria-labelledby="tm-features-title")
        .tm-section-head
          div
            p.tm-eyebrow
              span.tm-eyebrow-line
              span {{ t('org.landing.features.eyebrow') }}
            h2#tm-features-title.tm-h2 {{ t('org.landing.features.title') }}
          p.tm-section-lead {{ t('org.landing.features.lead') }}
        .tm-bento
          article.tm-card.span-7
            .tm-vis.tm-vis-billing(aria-hidden="true")
              .tm-wallet
                p.tm-vis-label {{ t('org.landing.features.items.billing.wallet') }}
                p.tm-wallet-value
                  span €372.00
                  span.tm-wallet-delta −€48.00
              .tm-wallet-lines
                i(v-for="n in 3" :key="n")
              ul.tm-wallet-members
                li(v-for="(name, index) in memberNames" :key="name" :style="{ '--i': index }")
                  Icon(name="lucide:check" aria-hidden="true")
                  span {{ name }}@acme.com
                  small {{ t('org.landing.features.items.billing.member') }}
            .tm-card-body
              h3.tm-h3 {{ t('org.landing.features.items.billing.title') }}
              p.tm-card-copy {{ t('org.landing.features.items.billing.desc') }}
          article.tm-card.span-5
            .tm-vis.tm-vis-invite(aria-hidden="true")
              .tm-chip.is-mail(:style="{ '--i': 0 }")
                Icon(name="lucide:mail" aria-hidden="true")
                span {{ t('org.landing.features.items.invite.email') }}
                em alice@acme.com
              .tm-chip.is-domain(:style="{ '--i': 1 }")
                Icon(name="lucide:shield-check" aria-hidden="true")
                span {{ t('org.landing.features.items.invite.domain') }}
                em acme.com
              p.tm-chip-note(:style="{ '--i': 2 }")
                Icon(name="lucide:arrow-right" aria-hidden="true")
                span {{ t('org.landing.features.items.invite.join') }}
            .tm-card-body
              h3.tm-h3 {{ t('org.landing.features.items.invite.title') }}
              p.tm-card-copy {{ t('org.landing.features.items.invite.desc') }}
          article.tm-card.span-4
            .tm-vis.tm-vis-control(aria-hidden="true")
              .tm-control-row
                span.tm-control-email ben@acme.com
                span.tm-control-state
                  span.is-on {{ t('org.memberStatus.active') }}
                  span.is-off {{ t('org.memberStatus.suspended') }}
                span.tm-toggle
                  i
              .tm-control-actions
                span {{ t('org.landing.features.items.control.suspend') }}
                span {{ t('org.landing.features.items.control.resume') }}
                span
                  Icon(name="material-symbols:lock-reset" aria-hidden="true")
                  | {{ t('org.landing.features.items.control.reset') }}
            .tm-card-body
              h3.tm-h3 {{ t('org.landing.features.items.control.title') }}
              p.tm-card-copy {{ t('org.landing.features.items.control.desc') }}
          article.tm-card.span-4
            .tm-vis.tm-vis-audit(aria-hidden="true")
              ul
                li(v-for="(line, index) in auditSample" :key="index" :style="{ '--i': index }")
                  span.tm-audit-time {{ line.time }}
                  span {{ line.text }}
            .tm-card-body
              h3.tm-h3 {{ t('org.landing.features.items.audit.title') }}
              p.tm-card-copy {{ t('org.landing.features.items.audit.desc') }}
          article.tm-card.span-4
            .tm-vis.tm-vis-finance(aria-hidden="true")
              p.tm-vis-label {{ t('org.landing.features.items.finance.chart') }}
              ul.tm-bars
                li(v-for="(bar, index) in financeBars" :key="bar.name" :style="{ '--i': index, '--w': bar.width }")
                  span.tm-bar-name {{ bar.name }}
                  span.tm-bar-track
                    i
                  span.tm-bar-value {{ bar.value }}
            .tm-card-body
              h3.tm-h3 {{ t('org.landing.features.items.finance.title') }}
              p.tm-card-copy {{ t('org.landing.features.items.finance.desc') }}
          article.tm-card.is-slim.span-6
            .tm-card-icon
              Icon(name="lucide:globe-2" aria-hidden="true")
            .tm-card-body
              h3.tm-h3 {{ t('org.landing.features.items.domain.title') }}
              p.tm-card-copy {{ t('org.landing.features.items.domain.desc') }}
          article.tm-card.is-slim.span-6
            .tm-card-icon
              Icon(name="mdi:ticket-percent-outline" aria-hidden="true")
            .tm-card-body
              h3.tm-h3 {{ t('org.landing.features.items.volume.title') }}
              p.tm-card-copy {{ t('org.landing.features.items.volume.desc') }}
              button.tm-inline-link(type="button" @click="openVolumeChat")
                span {{ t('org.landing.features.items.volume.cta') }}
                Icon(name="lucide:message-circle" aria-hidden="true")

      section.tm-section.reveal(aria-labelledby="tm-lines-title")
        .tm-section-head
          div
            p.tm-eyebrow
              span.tm-eyebrow-line
              span {{ t('org.landing.lines.eyebrow') }}
            h2#tm-lines-title.tm-h2 {{ t('org.landing.lines.title') }}
          p.tm-section-lead {{ t('org.landing.lines.lead') }}
        TeamsLinesDemo

      section.tm-section.reveal(aria-labelledby="tm-cases-title")
        .tm-section-head
          div
            p.tm-eyebrow
              span.tm-eyebrow-line
              span {{ t('org.landing.useCases.eyebrow') }}
            h2#tm-cases-title.tm-h2 {{ t('org.landing.useCases.title') }}
        .tm-cases
          article.tm-case(v-for="useCase in useCases" :key="useCase.id")
            .tm-card-icon
              Icon(:name="useCase.icon" aria-hidden="true")
            h3.tm-h3 {{ useCase.title }}
            p.tm-card-copy {{ useCase.desc }}

      section.tm-section.reveal(aria-labelledby="tm-faq-title")
        .tm-section-head
          div
            h2#tm-faq-title.tm-h2.is-small {{ t('org.landing.faq.title') }}
          p.tm-section-lead {{ t('org.landing.faq.subtitle') }}
        dl.tm-faq
          .tm-faq-item(v-for="item in faqItems" :key="item.id")
            dt {{ item.q }}
            dd {{ item.a }}

      section.tm-final.reveal(aria-labelledby="tm-final-title")
        h2#tm-final-title.tm-final-title {{ t('org.landing.final.title') }}
        p.tm-final-copy {{ t('org.landing.final.subtitle') }}
        .tm-final-actions
          button.tm-btn.is-primary(type="button" @click="startNow")
            span {{ t('org.landing.hero.cta_primary') }}
            Icon(name="lucide:arrow-right" aria-hidden="true")
          NuxtLink.tm-btn.is-ghost(to="/dedicated-servers") {{ t('org.landing.hero.cta_dedicated') }}

  SignupLoginModal(v-model="signModalVisible" defaultState="signup")
</template>

<script setup lang="ts">
const { t, locale } = useI18n()
const session = useVisitorSession()
const signModalVisible = ref(false)

const numbers = computed(() => (['n1', 'n2', 'n3', 'n4'] as const).map(key => ({
  key,
  value: t(`org.landing.numbers.${key}.value`),
  unit: t(`org.landing.numbers.${key}.unit`),
  label: t(`org.landing.numbers.${key}.label`),
})))

const flowSteps = computed(() => (['step1', 'step2', 'step3', 'step4'] as const).map(id => ({
  id,
  title: t(`org.landing.flow.steps.${id}.title`),
  desc: t(`org.landing.flow.steps.${id}.desc`),
})))

const memberNames = ['alice', 'ben', 'chloe'] as const

const auditSample = computed(() => [
  { time: '09:41', text: t('org.landing.demo.audit_invited', { actor: 'owner', n: 3 }) },
  { time: '09:52', text: t('org.landing.demo.audit_joined', { name: 'alice' }) },
  { time: '10:07', text: t('org.landing.demo.audit_activated', { actor: 'owner', n: 3 }) },
])

const financeBars = [
  { name: 'alice', width: 0.62, value: '€32' },
  { name: 'ben', width: 1, value: '€48' },
  { name: 'chloe', width: 0.34, value: '€16' },
  { name: 'dan', width: 0.62, value: '€32' },
] as const

const useCases = computed(() => [
  { id: 'small', icon: 'lucide:users', title: t('org.landing.useCases.items.small.title'), desc: t('org.landing.useCases.items.small.desc') },
  { id: 'company', icon: 'lucide:building-2', title: t('org.landing.useCases.items.company.title'), desc: t('org.landing.useCases.items.company.desc') },
])

const faqItems = computed(() => (['q1', 'q2', 'q3', 'q4'] as const).map(id => ({
  id,
  q: t(`org.landing.faq.items.${id}.q`),
  a: t(`org.landing.faq.items.${id}.a`),
})))

function startNow() {
  if (session.loggedIn) return navigateTo('/orgdashboard')
  signModalVisible.value = true
}

/** Same live-chat entry the pricing and dedicated-server pages use: open Intercom with the question prefilled. */
function openVolumeChat() {
  useIntercom().showNewMessage(t('org.landing.features.items.volume.message'))
}

let revealObserver: IntersectionObserver | null = null

onMounted(() => {
  const targets = Array.from(document.querySelectorAll<HTMLElement>('.tm-shell .reveal'))
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    targets.forEach(element => element.classList.add('is-visible'))
    return
  }
  revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible')
        revealObserver?.unobserve(entry.target)
      }
    })
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' })
  targets.forEach(element => revealObserver?.observe(element))
})

onBeforeUnmount(() => {
  revealObserver?.disconnect()
})

const pageTitle = computed(() => `${t('org.landing.seoTitle')} · ${t('site_name')}`)
const pageDescription = computed(() => t('org.landing.seoDescription'))

useSeoMeta({
  title: () => pageTitle.value,
  description: () => pageDescription.value,
  ogTitle: () => pageTitle.value,
  ogDescription: () => pageDescription.value,
  twitterTitle: () => pageTitle.value,
  twitterDescription: () => pageDescription.value,
  twitterCard: 'summary_large_image',
})

useHead({
  title: () => pageTitle.value,
})
</script>

<style lang="postcss" scoped>

.tm-shell {
  --tm-bg: var(--landing-background);
  --tm-surface: #0b0e0a;
  --tm-surface-2: #161a15;
  --tm-text: #f5f7f1;
  --tm-copy: #969f92;
  --tm-faint: #626a5e;
  --tm-line: rgba(255, 255, 255, 0.08);
  --tm-line-strong: rgba(255, 255, 255, 0.16);
  --tm-green: #7ed321;
  --tm-green-ink: #b9ef6a;
  --tm-green-soft: rgba(126, 211, 33, 0.16);
  --tm-on-green: #071006;
  --tm-display: Metropolis, 'SF Pro Display', 'Segoe UI', sans-serif;
  --tm-ease: cubic-bezier(0.16, 1, 0.3, 1);

  position: relative;
  width: 100%;
  min-width: 100%;
  align-self: stretch;
  overflow: clip;
  background: var(--tm-bg);
  color: var(--tm-text);
}

.tm-shell::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(60rem 28rem at 12% -6%, rgba(126, 211, 33, 0.09), transparent 62%),
    linear-gradient(rgba(126, 211, 33, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(126, 211, 33, 0.045) 1px, transparent 1px);
  background-size: auto, 48px 48px, 48px 48px;
  mask-image: linear-gradient(180deg, #000 0%, #000 42%, transparent 100%);
}

.light .tm-shell {
  --tm-surface: #ffffff;
  --tm-surface-2: #eef2f5;
  --tm-text: #1e293b;
  --tm-copy: #64748b;
  --tm-faint: #94a3b8;
  --tm-line: rgba(51, 65, 85, 0.14);
  --tm-line-strong: rgba(51, 65, 85, 0.24);
  --tm-green: #2d5a27;
  --tm-green-ink: #2d5a27;
  --tm-green-soft: rgba(45, 90, 39, 0.12);
  --tm-on-green: #f6fff0;
}

.light .tm-shell::before {
  background:
    radial-gradient(60rem 28rem at 12% -6%, rgba(45, 90, 39, 0.07), transparent 62%),
    linear-gradient(rgba(51, 65, 85, 0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(51, 65, 85, 0.07) 1px, transparent 1px);
  background-size: auto, 40px 40px, 40px 40px;
}

.tm-main {
  position: relative;
  z-index: 1;
}

.tm-frame {
  width: var(--marketing-frame);
  margin-inline: auto;
}

/* ---------- type ---------- */

.tm-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--tm-copy);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 1;
}

.tm-eyebrow-line {
  flex: none;
  width: 2.6rem;
  height: 1px;
  background: rgba(126, 211, 33, 0.52);
}

.tm-title,
.tm-h2,
.tm-h3,
.tm-number-value,
.tm-final-title {
  font-family: var(--tm-display);
}

.tm-title {
  display: grid;
  margin-top: 1.5rem;
  /* Capped so "Your whole team." stays on one line beside the demo at the 1180px frame. */
  font-size: clamp(2.7rem, 4.2vw, 4.3rem);
  font-weight: 700;
  line-height: 0.96;
  letter-spacing: -0.05em;
  text-wrap: balance;
}

.tm-title-line.is-accent {
  color: var(--tm-green);
}

.tm-lead {
  max-width: 32rem;
  margin-top: 1.6rem;
  color: var(--tm-copy);
  font-size: clamp(1rem, 1.3vw, 1.15rem);
  line-height: 1.72;
}

.tm-h2 {
  font-size: clamp(2rem, 3.4vw, 3.15rem);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -0.04em;
  text-wrap: balance;
}

.tm-h2.is-small {
  font-size: clamp(1.7rem, 2.6vw, 2.4rem);
}

.tm-h3 {
  font-size: 1.18rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.tm-section-lead,
.tm-card-copy,
.tm-flow-desc {
  color: var(--tm-copy);
  line-height: 1.68;
}

.tm-shell.is-zh .tm-title,
.tm-shell.is-zh .tm-h2,
.tm-shell.is-zh .tm-h3,
.tm-shell.is-zh .tm-final-title {
  font-family: 'PingFang SC', 'Hiragino Sans GB', 'Noto Sans SC', 'Source Han Sans SC', 'Microsoft YaHei', sans-serif;
  letter-spacing: normal;
}

.tm-shell.is-zh .tm-title {
  font-size: clamp(2.4rem, 4vw, 3.7rem);
  line-height: 1.14;
}

@media (min-width: 1081px) {
  .tm-shell.is-zh .tm-title-line {
    white-space: nowrap;
  }
}

.tm-shell.is-zh .tm-h2 {
  line-height: 1.2;
}

.tm-shell.is-zh .tm-lead {
  line-height: 1.85;
}

/* ---------- hero ---------- */

.tm-hero {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
  align-items: center;
  gap: clamp(2.5rem, 5vw, 5rem);
  padding-block: clamp(3.5rem, 7vw, 6.5rem) clamp(2.5rem, 5vw, 4rem);
}

.tm-hero-note {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  max-width: 32rem;
  margin-top: 1.5rem;
  color: var(--tm-faint);
  font-size: 0.84rem;
  line-height: 1.6;
}

.tm-note-icon {
  flex: none;
  width: 0.95rem;
  height: 0.95rem;
  margin-top: 0.2rem;
}

.tm-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2.1rem;
}

.tm-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 3rem;
  padding: 0.9rem 1.35rem;
  border-radius: 999px;
  font-family: var(--tm-display);
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition:
    transform 0.3s var(--tm-ease),
    background-color 0.3s var(--tm-ease),
    border-color 0.3s var(--tm-ease),
    color 0.3s var(--tm-ease);
}

.tm-btn :deep(svg) {
  width: 1rem;
  height: 1rem;
}

.tm-btn.is-primary {
  background: var(--tm-green);
  color: var(--tm-on-green);
}

.tm-btn.is-primary:hover {
  transform: translateY(-1px);
  background: var(--tm-green-ink);
}

.tm-btn.is-ghost {
  border: 1px solid var(--tm-line-strong);
  color: var(--tm-text);
}

.tm-btn.is-ghost:hover {
  transform: translateY(-1px);
  border-color: rgba(126, 211, 33, 0.5);
  background: var(--tm-green-soft);
}

.tm-btn:active {
  transform: translateY(1px) scale(0.985);
}

.tm-btn:focus-visible,
.tm-inline-link:focus-visible {
  outline: 2px solid rgba(126, 211, 33, 0.6);
  outline-offset: 3px;
}

.tm-hero-stage {
  min-width: 0;
}

/* ---------- numbers ---------- */

.tm-numbers {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-top: 1px solid var(--tm-line-strong);
  border-bottom: 1px solid var(--tm-line-strong);
}

.tm-number {
  padding: 2rem 1.5rem 2rem 0;
  border-right: 1px solid var(--tm-line);
  opacity: 0;
  transform: translate3d(0, 1rem, 0);
  transition:
    opacity 0.7s var(--tm-ease) var(--delay, 0ms),
    transform 0.7s var(--tm-ease) var(--delay, 0ms);
}

.reveal.is-visible .tm-number {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

.tm-number + .tm-number {
  padding-left: 1.5rem;
}

.tm-number:last-child {
  border-right: 0;
}

.tm-number-value {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  font-size: clamp(2.4rem, 4vw, 3.8rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.035em;
  font-variant-numeric: tabular-nums;
}

.tm-number-unit {
  color: var(--tm-green);
  font-size: 0.42em;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.tm-number-label {
  margin-top: 0.85rem;
  color: var(--tm-copy);
  font-size: 0.9rem;
  line-height: 1.5;
}

/* ---------- sections ---------- */

.tm-section {
  padding-block: clamp(4rem, 8vw, 7rem) 0;
}

.tm-section-head {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  align-items: end;
  gap: 1.5rem 3rem;
  margin-bottom: clamp(2.2rem, 4vw, 3.4rem);
}

.tm-section-head .tm-h2 {
  margin-top: 1.1rem;
}

.tm-section-lead {
  max-width: 30rem;
  font-size: 1rem;
}

/* ---------- flow ---------- */

.tm-flow {
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tm-flow::before,
.tm-flow::after {
  content: '';
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  right: 1.5rem;
  height: 1px;
  pointer-events: none;
}

.tm-flow::before {
  background: var(--tm-line-strong);
}

.tm-flow::after {
  background: linear-gradient(90deg, var(--tm-green), rgba(126, 211, 33, 0.35));
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 1.8s var(--tm-ease) 0.3s;
}

.reveal.is-visible .tm-flow::after {
  transform: scaleX(1);
}

.tm-flow-step {
  position: relative;
  min-width: 0;
}

.tm-flow-node {
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  width: 3rem;
  height: 3rem;
  border: 1px solid var(--tm-line-strong);
  border-radius: 999px;
  /* Opaque page colour so the rail behind never shows through; the green tint is an inset shadow. */
  background: var(--tm-bg);
  box-shadow: inset 0 0 0 1.5rem transparent;
  transition:
    border-color 0.5s var(--tm-ease) calc(0.3s + var(--i) * 0.42s),
    box-shadow 0.5s var(--tm-ease) calc(0.3s + var(--i) * 0.42s);
}

.tm-flow-index {
  font-family: var(--tm-display);
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: var(--tm-copy);
  transition: color 0.5s var(--tm-ease) calc(0.3s + var(--i) * 0.42s);
}

.reveal.is-visible .tm-flow-node {
  border-color: var(--tm-green);
  box-shadow:
    inset 0 0 0 1.5rem var(--tm-green-soft),
    0 0 0 0.35rem rgba(126, 211, 33, 0.08);
}

.reveal.is-visible .tm-flow-index {
  color: var(--tm-green);
}

.tm-flow-step .tm-h3 {
  margin-top: 1.4rem;
}

.tm-flow-desc {
  margin-top: 0.5rem;
  font-size: 0.92rem;
}

/* ---------- bento ---------- */

.tm-bento {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 1rem;
}

.tm-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--tm-line);
  border-radius: 16px;
  background: var(--tm-surface);
  transition:
    border-color 0.4s var(--tm-ease),
    transform 0.4s var(--tm-ease);
}

.tm-card:hover {
  border-color: rgba(126, 211, 33, 0.35);
  transform: translateY(-2px);
}

.tm-card.span-4 {
  grid-column: span 4;
}

.tm-card.span-5 {
  grid-column: span 5;
}

.tm-card.span-6 {
  grid-column: span 6;
}

.tm-card.span-7 {
  grid-column: span 7;
}

.tm-card-body {
  padding: 1.35rem 1.5rem 1.5rem;
}

.tm-card-body .tm-card-copy {
  margin-top: 0.55rem;
  font-size: 0.92rem;
}

.tm-card.is-slim {
  flex-direction: row;
  align-items: flex-start;
  gap: 0.25rem;
  padding: 0.35rem 0.35rem 0.35rem 1.35rem;
}

.tm-card.is-slim .tm-card-body {
  padding-left: 0.85rem;
}

.tm-card-icon {
  display: grid;
  place-items: center;
  flex: none;
  width: 2.6rem;
  height: 2.6rem;
  margin-top: 1.35rem;
  border: 1px solid var(--tm-line-strong);
  border-radius: 12px;
  color: var(--tm-green);
}

.tm-card-icon :deep(svg) {
  width: 1.2rem;
  height: 1.2rem;
}

.tm-inline-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.75rem;
  padding: 0;
  border: 0;
  background: none;
  color: var(--tm-green);
  font: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
}

.tm-inline-link :deep(svg) {
  width: 0.95rem;
  height: 0.95rem;
  transition: transform 0.3s var(--tm-ease);
}

.tm-inline-link:hover :deep(svg) {
  transform: translateY(-2px);
}

.tm-vis {
  position: relative;
  flex: 1 0 auto;
  min-height: 11.5rem;
  padding: 1.35rem 1.5rem 0;
  font-size: 0.78rem;
}

.tm-vis-label {
  color: var(--tm-faint);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* billing: one balance, three members */

.tm-vis-billing {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) 2.5rem minmax(0, 1.1fr);
  align-items: center;
  gap: 0.5rem;
}

.tm-wallet {
  padding: 0.9rem 1rem;
  border: 1px solid var(--tm-line-strong);
  border-radius: 12px;
  background: var(--tm-surface-2);
}

.tm-wallet-value {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
  margin-top: 0.45rem;
  font-family: var(--tm-display);
  font-size: 1.35rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.tm-wallet-delta {
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  background: var(--tm-green-soft);
  color: var(--tm-green);
  font-size: 0.68rem;
  font-weight: 800;
  opacity: 0;
  transform: translate3d(-0.3rem, 0, 0);
  transition:
    opacity 0.5s var(--tm-ease) 1.4s,
    transform 0.5s var(--tm-ease) 1.4s;
}

.reveal.is-visible .tm-wallet-delta {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

.tm-wallet-lines {
  display: grid;
  gap: 0.9rem;
}

.tm-wallet-lines i {
  display: block;
  height: 1px;
  background: linear-gradient(90deg, var(--tm-line-strong), var(--tm-green));
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.6s var(--tm-ease);
}

.tm-wallet-lines i:nth-child(1) {
  transition-delay: 0.5s;
}

.tm-wallet-lines i:nth-child(2) {
  transition-delay: 0.75s;
}

.tm-wallet-lines i:nth-child(3) {
  transition-delay: 1s;
}

.reveal.is-visible .tm-wallet-lines i {
  transform: scaleX(1);
}

.tm-wallet-members {
  display: grid;
  gap: 0.4rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tm-wallet-members li {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--tm-line);
  border-radius: 10px;
  white-space: nowrap;
  opacity: 0;
  transform: translate3d(0.4rem, 0, 0);
  transition:
    opacity 0.5s var(--tm-ease) calc(0.8s + var(--i) * 0.25s),
    transform 0.5s var(--tm-ease) calc(0.8s + var(--i) * 0.25s),
    border-color 0.5s var(--tm-ease) calc(0.8s + var(--i) * 0.25s);
}

.tm-wallet-members li :deep(svg) {
  flex: none;
  width: 0.8rem;
  height: 0.8rem;
  color: var(--tm-green);
}

.tm-wallet-members li span {
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}

.tm-wallet-members li small {
  margin-left: auto;
  color: var(--tm-faint);
  font-size: 0.66rem;
}

.reveal.is-visible .tm-wallet-members li {
  opacity: 1;
  transform: translate3d(0, 0, 0);
  border-color: rgba(126, 211, 33, 0.35);
}

/* invite: email chip, verified domain */

.tm-vis-invite {
  display: grid;
  align-content: center;
  gap: 0.6rem;
}

.tm-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.85rem;
  border: 1px solid var(--tm-line-strong);
  border-radius: 12px;
  background: var(--tm-surface-2);
  white-space: nowrap;
  opacity: 0;
  transform: translate3d(0, 0.6rem, 0);
  transition:
    opacity 0.55s var(--tm-ease) calc(0.3s + var(--i) * 0.25s),
    transform 0.55s var(--tm-ease) calc(0.3s + var(--i) * 0.25s);
}

.tm-chip :deep(svg) {
  flex: none;
  width: 0.95rem;
  height: 0.95rem;
  color: var(--tm-green);
}

.tm-chip span {
  font-weight: 700;
}

.tm-chip em {
  margin-left: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--tm-copy);
  font-style: normal;
}

.tm-chip-note {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding-left: 0.85rem;
  color: var(--tm-copy);
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.55s var(--tm-ease) calc(0.3s + var(--i) * 0.25s);
}

.tm-chip-note :deep(svg) {
  width: 0.85rem;
  height: 0.85rem;
  color: var(--tm-green);
}

.reveal.is-visible .tm-chip,
.reveal.is-visible .tm-chip-note {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

/* control: suspend / resume toggle */

.tm-vis-control {
  display: grid;
  align-content: center;
  gap: 0.75rem;
}

.tm-control-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid var(--tm-line-strong);
  border-radius: 12px;
  background: var(--tm-surface-2);
}

.tm-control-email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.tm-control-state {
  position: relative;
  flex: none;
  margin-left: auto;
  width: 4.25rem;
  height: 1.1rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-align: right;
}

.tm-control-state span {
  position: absolute;
  inset: 0;
  line-height: 1.1rem;
}

.tm-control-state .is-on {
  color: var(--tm-green);
}

.tm-control-state .is-off {
  color: #e8b04a;
  opacity: 0;
}

.tm-toggle {
  position: relative;
  flex: none;
  width: 2.1rem;
  height: 1.2rem;
  border-radius: 999px;
  background: var(--tm-green);
}

.tm-toggle i {
  position: absolute;
  top: 0.15rem;
  left: 0.15rem;
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 999px;
  background: #fff;
  transform: translateX(0.9rem);
}

.reveal.is-visible .tm-toggle {
  animation: tm-toggle-track 6s var(--tm-ease) 1.2s infinite;
}

.reveal.is-visible .tm-toggle i {
  animation: tm-toggle-knob 6s var(--tm-ease) 1.2s infinite;
}

.reveal.is-visible .tm-control-state .is-on {
  animation: tm-state-on 6s var(--tm-ease) 1.2s infinite;
}

.reveal.is-visible .tm-control-state .is-off {
  animation: tm-state-off 6s var(--tm-ease) 1.2s infinite;
}

@keyframes tm-toggle-track {
  0%, 25% {
    background: var(--tm-green);
  }

  33%, 66% {
    background: var(--tm-line-strong);
  }

  75%, 100% {
    background: var(--tm-green);
  }
}

@keyframes tm-toggle-knob {
  0%, 25% {
    transform: translateX(0.9rem);
  }

  33%, 66% {
    transform: translateX(0);
  }

  75%, 100% {
    transform: translateX(0.9rem);
  }
}

@keyframes tm-state-on {
  0%, 25% {
    opacity: 1;
  }

  33%, 66% {
    opacity: 0;
  }

  75%, 100% {
    opacity: 1;
  }
}

@keyframes tm-state-off {
  0%, 25% {
    opacity: 0;
  }

  33%, 66% {
    opacity: 1;
  }

  75%, 100% {
    opacity: 0;
  }
}

.tm-control-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.tm-control-actions span {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.35rem 0.6rem;
  border: 1px solid var(--tm-line);
  border-radius: 8px;
  color: var(--tm-copy);
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}

.tm-control-actions span :deep(svg) {
  width: 0.8rem;
  height: 0.8rem;
}

/* audit: timestamped lines */

.tm-vis-audit {
  display: grid;
  align-content: center;
}

.tm-vis-audit ul {
  display: grid;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tm-vis-audit li {
  display: flex;
  gap: 0.6rem;
  padding: 0.55rem 0.75rem;
  border-left: 2px solid var(--tm-line-strong);
  color: var(--tm-copy);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
  transform: translate3d(-0.4rem, 0, 0);
  transition:
    opacity 0.5s var(--tm-ease) calc(0.3s + var(--i) * 0.3s),
    transform 0.5s var(--tm-ease) calc(0.3s + var(--i) * 0.3s),
    border-color 0.5s var(--tm-ease) calc(0.3s + var(--i) * 0.3s);
}

.tm-vis-audit li span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
}

.reveal.is-visible .tm-vis-audit li {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

.reveal.is-visible .tm-vis-audit li:last-child {
  border-color: var(--tm-green);
  color: var(--tm-text);
}

.tm-audit-time {
  flex: none;
  color: var(--tm-faint);
  font-variant-numeric: tabular-nums;
}

/* finance: bars by member */

.tm-vis-finance {
  display: grid;
  align-content: center;
  gap: 0.9rem;
}

.tm-bars {
  display: grid;
  gap: 0.55rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tm-bars li {
  display: grid;
  grid-template-columns: 3rem minmax(0, 1fr) 2.4rem;
  align-items: center;
  gap: 0.6rem;
}

.tm-bar-name {
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--tm-copy);
  font-weight: 600;
}

.tm-bar-track {
  display: block;
  height: 0.5rem;
  border-radius: 999px;
  background: var(--tm-surface-2);
  overflow: hidden;
}

.tm-bar-track i {
  display: block;
  width: calc(var(--w) * 100%);
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, rgba(126, 211, 33, 0.55), var(--tm-green));
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.9s var(--tm-ease) calc(0.3s + var(--i) * 0.12s);
}

.reveal.is-visible .tm-bar-track i {
  transform: scaleX(1);
}

.tm-bar-value {
  text-align: right;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* ---------- use cases ---------- */

.tm-cases {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.tm-case {
  padding: 0 1.5rem 1.6rem;
  border: 1px solid var(--tm-line);
  border-radius: 16px;
  background: var(--tm-surface);
  transition:
    border-color 0.4s var(--tm-ease),
    transform 0.4s var(--tm-ease);
}

.tm-case:hover {
  border-color: rgba(126, 211, 33, 0.35);
  transform: translateY(-2px);
}

.tm-case .tm-h3 {
  margin-top: 1.1rem;
}

.tm-case .tm-card-copy {
  margin-top: 0.5rem;
  font-size: 0.95rem;
}

/* ---------- faq ---------- */

.tm-faq {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 3rem;
  margin: 0;
}

.tm-faq-item {
  padding: 1.5rem 0;
  border-top: 1px solid var(--tm-line);
}

.tm-faq-item dt {
  font-size: 1.02rem;
  font-weight: 700;
  line-height: 1.4;
}

.tm-faq-item dd {
  margin: 0.55rem 0 0;
  color: var(--tm-copy);
  font-size: 0.93rem;
  line-height: 1.7;
}

/* ---------- final ---------- */

.tm-final {
  display: grid;
  justify-items: center;
  padding-block: clamp(4rem, 8vw, 7rem) clamp(4rem, 8vw, 7rem);
  text-align: center;
}

.tm-final-title {
  font-size: clamp(1.9rem, 3.2vw, 2.9rem);
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1.05;
}

.tm-final-copy {
  max-width: 34rem;
  margin-top: 1rem;
  color: var(--tm-copy);
  line-height: 1.7;
}

.tm-final-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2rem;
}

/* ---------- reveal ---------- */

.reveal {
  opacity: 0;
  transform: translate3d(0, 2rem, 0);
  transition:
    opacity 0.75s var(--tm-ease),
    transform 0.75s var(--tm-ease);
}

.reveal.is-visible {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal,
  .tm-number,
  .tm-flow::after,
  .tm-flow-node,
  .tm-flow-index,
  .tm-wallet-delta,
  .tm-wallet-lines i,
  .tm-wallet-members li,
  .tm-chip,
  .tm-chip-note,
  .tm-vis-audit li,
  .tm-bar-track i,
  .tm-card,
  .tm-case {
    transition: none;
  }

  .reveal.is-visible .tm-toggle,
  .reveal.is-visible .tm-toggle i,
  .reveal.is-visible .tm-control-state .is-on,
  .reveal.is-visible .tm-control-state .is-off {
    animation: none;
  }
}

/* ---------- responsive ---------- */

@media (max-width: 1080px) {
  .tm-card.span-4,
  .tm-card.span-5,
  .tm-card.span-7 {
    grid-column: span 6;
  }

  .tm-vis-billing {
    grid-template-columns: minmax(0, 1fr);
  }

  .tm-wallet-lines {
    display: none;
  }
}

@media (max-width: 767px) {
  .tm-hero {
    grid-template-columns: minmax(0, 1fr);
  }

  .tm-hero-stage {
    max-width: 44rem;
  }

  .tm-section-head {
    grid-template-columns: minmax(0, 1fr);
    align-items: start;
  }

  .tm-numbers {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .tm-number {
    padding: 1.6rem 1.25rem 1.6rem 0;
    border-bottom: 1px solid var(--tm-line);
  }

  .tm-number:nth-child(2n) {
    padding-left: 1.25rem;
    border-right: 0;
  }

  .tm-number:nth-child(2n + 1) {
    padding-left: 0;
  }

  .tm-number:nth-child(n + 3) {
    border-bottom: 0;
  }

  .tm-flow {
    grid-template-columns: minmax(0, 1fr);
    gap: 2.2rem;
    padding-left: 4.2rem;
  }

  .tm-flow::before,
  .tm-flow::after {
    top: 1.5rem;
    bottom: 1.5rem;
    left: 1.5rem;
    right: auto;
    width: 1px;
    height: auto;
  }

  .tm-flow::after {
    background: linear-gradient(180deg, var(--tm-green), rgba(126, 211, 33, 0.35));
    transform: scaleY(0);
    transform-origin: center top;
  }

  .reveal.is-visible .tm-flow::after {
    transform: scaleY(1);
  }

  .tm-flow-node {
    position: absolute;
    top: 0;
    left: -4.2rem;
  }

  .tm-flow-step .tm-h3 {
    margin-top: 0;
    padding-top: 0.75rem;
  }

  .tm-card.span-4,
  .tm-card.span-5,
  .tm-card.span-6,
  .tm-card.span-7 {
    grid-column: span 12;
  }

  .tm-cases,
  .tm-faq {
    grid-template-columns: minmax(0, 1fr);
  }

  .tm-vis {
    padding-inline: 1.1rem;
  }

  .tm-card-body {
    padding-inline: 1.1rem;
  }

  .tm-actions .tm-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
